import { useEffect, useMemo, useState } from 'react';

import TableHeader from '@src/components/TableHeader';
import useGetAttendUsers from '@src/hooks/queries/attend/useGetAttendUsers';
import { closeModal, openModal } from '@src/reduce/modals';
import { Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { off, on } from '@utils/globalEvents';

import * as C from '@components/Common';

import * as S from './styled';
import * as T from './type';

interface DelegateManageEventModalProps {
  groupEventId: number;
  title: string;
}

const DelegateManageEventModal = ({ groupEventId, title }: DelegateManageEventModalProps) => {
  const PAGE_SIZE = 8;

  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);
  const [selectedMembers, setSelectedMembers] = useState<T.AttendUserTableDataSource>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>();

  const {
    data: attendUserList,
    total,
    isLoading,
    refetch,
  } = useGetAttendUsers({ groupEventId }, { page, q: searchValue, isDelegator: true });

  const {
    data: attendUserAllList,
    refetch: refetchAll,
    isLoading: isLoadingAll,
  } = useGetAttendUsers({ groupEventId }, { limit: 5000, isDelegator: true });

  const searchOrg = (value: string) => {
    if (value.length < 2) {
      message.info('검색어는 2글자 이상이어야 합니다.');
      return false;
    }

    setPage(1);

    setSearchValue(value);
  };

  const excelDownload = () => {
    const excelList = attendUserAllList.reduce((accumulator: T.excelProps[], userList) => {
      const kcplaaMember = userList.groupMember?.user?.kcplaaMember;

      const newObj = {
        소속: userList.organization || '',
        성명: userList.name,
        기수: kcplaaMember
          ? kcplaaMember?.licenseCb
            ? kcplaaMember.licenseCb + '기'
            : '경력'
          : '',
        자격번호: kcplaaMember?.licenseNo || '',
        휴대폰번호: userList.phone,
        이메일: userList.email,
        위임일시: dayjs(userList.attendanceTime).format('YYYY.MM.DD HH:mm'),
        '위임받는 자': userList.delegator,
        '위임받는 자 소속': userList.delegatorAssociation,
      };
      accumulator.push(newObj);
      return accumulator;
    }, []);
    const worksheet = XLSX.utils.json_to_sheet(excelList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const excelData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    // 엑셀 파일 다운로드
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(excelData);
    downloadLink.download = `${title} - 위임자 목록.xlsx`;
    downloadLink.click();
  };

  const openConfirmAlertModal = () => {
    const groupEventMemberIds = selectedMembers.map(v => v.id);
    if (!groupEventMemberIds.length) return;

    dispatch(
      openModal({
        name: 'confirmAlertModal',
        props: {
          text: '삭제하시겠습니까?',
          isConfirm: true,
          groupEventId,
          groupEventMemberIds,
        },
      }),
    );
  };

  const headerButtons = [
    {
      text: '삭제',
      buttonProps: {
        name: 'delete',
        onClick: openConfirmAlertModal,
      },
    },
  ];

  const rightButtons = [
    {
      text: '목록 내려받기',
      buttonProps: {
        style: { color: '#ffffff', background: '#2d3f86' },
        onClick: excelDownload,
        disabled: isLoadingAll,
      },
    },
  ];

  const inputProps = {
    placeholder: '이름 검색',
    onSearch: (value: string) => searchOrg(value),
    style: { order: '1', width: '100%', marginLeft: '0' },
  };

  const close = () => {
    dispatch(closeModal('delegateManageEventModal'));
  };

  const select = (data: T.AttendUserTableData, selected: boolean) => {
    setSelectedMembers(prevState => {
      if (selected) return [...prevState, data];
      const findIndex = prevState.findIndex(v => v.id === data.id);
      if (typeof findIndex === 'undefined') return prevState;

      return update(prevState, {
        $splice: [[findIndex, 1]],
      });
    });
  };

  const selectAll = (selected: boolean, data: T.AttendUserTableDataSource) => {
    setSelectedMembers(prevState => (selected ? [...prevState, ...data.filter(v => v)] : []));
  };

  const dataSource: T.AttendUserTableDataSource = useMemo(() => {
    console.log(attendUserList);
    return attendUserList.map(v => ({ ...v, key: v.id }));
  }, [attendUserList]);

  const columns = useMemo<ColumnsType<T.AttendUserTableData>>(() => {
    const columnsValue: ColumnsType<T.AttendUserTableData> = [
      {
        key: 'organization',
        dataIndex: 'organization',
        title: '소속',
        width: '11%',
        ellipsis: true,
        render: organization => {
          return <>{organization || ''}</>;
        },
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: '성명',
        width: '10%',
        ellipsis: true,
        render: (name, record) => {
          return (
            <S.ApplyMemberName
              {...(record.isDiligence && { title: '✓ 아이콘은 성실 회원을 표시합니다.' })}>
              {record.isDiligence && <C.CertifiedBadge />}
              {name}
            </S.ApplyMemberName>
          );
        },
      },
      {
        key: 'phone',
        dataIndex: 'phone',
        title: '휴대폰번호',
        width: '14%',
        ellipsis: true,
        render: phone => {
          return <>{phone.split('-').join('')}</>;
        },
      },
      {
        key: 'email',
        dataIndex: 'email',
        title: '이메일',
        width: '15%',
        ellipsis: true,
        render: email => {
          return <>{email}</>;
        },
      },
      {
        key: 'licenseCb',
        dataIndex: 'licenseCb',
        title: '기수',
        width: '9%',
        ellipsis: true,
        render: (_, record) => {
          const licenseCb = record.groupMember?.user?.kcplaaMember?.licenseCb;
          return <>{licenseCb ? licenseCb + '기' : '경력'}</>;
        },
      },
      {
        key: 'attendanceTime',
        dataIndex: 'attendanceTime',
        title: '위임일시',
        width: '15%',
        render: attendanceTime => {
          const attendanceTimeYMD = dayjs(attendanceTime).format('YYYY.MM.DD HH:mm');
          return <>{attendanceTimeYMD}</>;
        },
      },
      {
        key: 'delegator',
        dataIndex: 'delegator',
        title: '위임받는 자',
        width: '10%',
        ellipsis: true,
        render: delegator => {
          return <>{delegator}</>;
        },
      },
      {
        key: 'delegatorAssociation',
        dataIndex: 'delegatorAssociation',
        title: '위임받는 자 소속',
        width: '13%',
        ellipsis: true,
        render: delegatorAssociation => {
          return <>{delegatorAssociation}</>;
        },
      },
    ];

    return columnsValue;
  }, [attendUserList]);

  const successUpdate = () => {
    refetch();
    refetchAll();
    setSelectedMembers([]);
  };

  useEffect(() => {
    on('SUCCESS_UPDATE_ATTEND_USER', successUpdate);

    return () => {
      off('SUCCESS_UPDATE_ATTEND_USER', successUpdate);
    };
  }, []);

  return (
    <S.ModalContainer open={true} title='위임자 관리' footer={null} width='1200px' onCancel={close}>
      <TableHeader
        buttons={headerButtons}
        rightButtons={rightButtons}
        searchInput={true}
        inputProps={inputProps}
      />
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        pagination={{
          total,
          position: ['bottomCenter'],
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          onChange: setPage,
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedMembers.map(v => v.key),
          onSelect: select,
          onSelectAll: selectAll,
        }}
      />
    </S.ModalContainer>
  );
};
export default DelegateManageEventModal;
